import { prefersReducedMotion } from '../store/settings.js';

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/**
 * Tween a number into `node`'s text. Snaps straight to the value when the
 * user prefers reduced motion. Returns a cancel fn.
 */
export function countUp(
  node,
  to,
  { from = 0, duration = 550, decimals = 0, prefix = '', suffix = '', ease = easeOutCubic } = {},
) {
  const fmt = (v) => `${prefix}${decimals ? v.toFixed(decimals) : Math.round(v)}${suffix}`;
  if (prefersReducedMotion() || duration <= 0 || from === to) {
    node.textContent = fmt(to);
    return () => {};
  }
  let raf = 0;
  const t0 = performance.now();
  const tick = (now) => {
    const p = Math.min(1, (now - t0) / duration);
    node.textContent = fmt(from + (to - from) * ease(p));
    if (p < 1) raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(raf);
}

/**
 * Add a sliding pill behind an already-built `.seg` (its `.seg__btn`
 * children can be unequal widths — the thumb measures them). Returns a
 * setter `setActive(index)`. The move itself is a CSS transition, so
 * reduced-motion users just get an instant jump.
 */
export function segThumb(seg, _count, active = 0) {
  seg.classList.add('seg--thumbed');
  const thumb = document.createElement('div');
  thumb.className = 'seg__thumb';
  seg.prepend(thumb);
  let current = active;
  const place = (i, tries = 0) => {
    current = i;
    const b = seg.querySelectorAll('.seg__btn')[i];
    if (b && b.offsetWidth) {
      thumb.style.width = `${b.offsetWidth}px`;
      thumb.style.transform = `translateX(${b.offsetLeft - 3}px)`;
    } else if (tries < 20) {
      // Not laid out yet (hidden tab, webfont pending) — try again.
      requestAnimationFrame(() => place(i, tries + 1));
    }
  };
  requestAnimationFrame(() => place(active));
  if (document.fonts?.ready) document.fonts.ready.then(() => place(current));
  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(() => place(current));
    ro.observe(seg);
  }
  return place;
}

/**
 * A short WAAPI fade + rise on an element that was just inserted. No-op
 * under reduced motion.
 */
export function fadeSlideIn(node, { duration = 240, dy = 8 } = {}) {
  if (prefersReducedMotion() || !node.animate) return;
  node.animate(
    [
      { opacity: 0, transform: `translateY(${dy}px)` },
      { opacity: 1, transform: 'none' },
    ],
    { duration, easing: 'cubic-bezier(.22,1,.36,1)' },
  );
}
